import { Link } from "react-router-dom";
import {
  Upload, Calculator, MessageCircle, GraduationCap, ArrowRight,
  ShieldCheck, Truck, FileText, CheckCircle2, Star, Sparkles, Award,
} from "lucide-react";
import ServiceCard from "@/components/ServiceCard";
import { SERVICES, PACKAGES, WHATSAPP_LINK } from "@/lib/constants";
import { useSEO } from "@/lib/useSEO";

const HERO_IMG = "https://images.pexels.com/photos/14180701/pexels-photo-14180701.jpeg";
const GRAD_IMG = "https://images.pexels.com/photos/37410978/pexels-photo-37410978.jpeg";
const PRESS_IMG = "https://images.pexels.com/photos/1440504/pexels-photo-1440504.jpeg";

export default function Home() {
  useSEO({
    title: "ScholarPrintHub — Thesis Writing, Formatting, Printing & Hard Binding · Lucknow, India",
    description: "One platform for thesis assistance, synopsis preparation, research paper support, plagiarism check, formatting, PDF printing, hard binding with golden embossing, and doorstep delivery across India.",
    keywords: "thesis printing lucknow, hard binding online india, thesis writing assistance, synopsis writing, research paper support, plagiarism check thesis, thesis formatting india, PhD thesis print, dissertation binding, AKTU thesis",
    image: HERO_IMG,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Academic writing assistance, thesis printing and hard binding",
      "provider": { "@type": "LocalBusiness", "name": "ScholarPrintHub", "areaServed": "IN" },
      "offers": SERVICES.map((s) => ({ "@type": "Offer", "name": s.title, "description": s.tagline })),
    },
  });
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-slate-200 bg-white">
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
             style={{ backgroundImage: "linear-gradient(#0A192F 1px, transparent 1px), linear-gradient(90deg, #0A192F 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
        <div className="container-app pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-12 gap-10 items-center relative">
          <div className="lg:col-span-7 animate-fade-in-up">
            <span className="section-eyebrow">Lucknow · India · Since 2020</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mt-4 text-navy">
              Complete <span className="italic text-royal">Academic Writing</span>, Formatting, Printing &{" "}
              <span className="relative inline-block">
                <span>Binding</span>
                <span className="absolute -bottom-1 left-0 right-0 h-[6px] bg-gold/70 -z-0" />
              </span>{" "}
              Solution.
            </h1>
            <p className="mt-6 text-slate-600 max-w-2xl text-base sm:text-lg">
              Get thesis assistance, synopsis preparation, research paper support, plagiarism checking,
              formatting, PDF printing, hard binding, spiral binding, and doorstep delivery — all in one place.
            </p>

            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl">
              <Link to="/upload" data-testid="hero-upload-btn" className="btn-primary">
                <Upload className="w-4 h-4" /> Upload PDF
              </Link>
              <Link to="/academic-request" data-testid="hero-academic-btn" className="btn-outline">
                <GraduationCap className="w-4 h-4" /> Academic Help
              </Link>
              <Link to="/calculator" data-testid="hero-calc-btn" className="btn-gold">
                <Calculator className="w-4 h-4" /> Print Cost
              </Link>
              <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-btn"
                 className="btn-outline border-[#25D366] text-[#128C7E] hover:bg-[#25D366] hover:text-white">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            </div>

            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-sm">
              {[
                { icon: ShieldCheck, label: "Confidential handling" },
                { icon: FileText, label: "APA · MLA · IEEE · Vancouver" },
                { icon: Truck, label: "Doorstep delivery" },
                { icon: Award, label: "Gold embossing" },
              ].map((f, i) => (
                <div key={i} className="flex items-center gap-2 text-slate-600">
                  <f.icon className="w-4 h-4 text-gold" />
                  <span>{f.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative border border-slate-200 rounded-sm overflow-hidden">
              <img src={HERO_IMG} alt="Indian student reviewing thesis notes" className="w-full h-[520px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-white/40 via-transparent to-transparent" />
              <div className="absolute top-4 left-4 bg-white/95 backdrop-blur border border-slate-200 px-3 py-2 rounded-sm text-xs font-mono">
                <span className="text-royal">#</span> PROJECT / THESIS-2026-047
              </div>
              <div className="absolute bottom-4 left-4 right-4 bg-navy text-white px-4 py-3 rounded-sm flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-gold">Live estimate</div>
                  <div className="font-display text-2xl">₹ 2,480</div>
                </div>
                <div className="text-[10px] font-mono text-slate-300">
                  180 pgs · B/W · Hard bind · Gold emboss
                </div>
              </div>
            </div>
            <div className="hidden lg:flex absolute -bottom-6 -left-6 bg-gold text-navy px-4 py-3 rounded-sm border border-navy/10 items-center gap-2 shadow-sm">
              <Sparkles className="w-4 h-4" />
              <span className="font-mono text-xs">Instant cost. Real prints. Real delivery.</span>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="container-app py-20 lg:py-28" id="services">
        <div className="grid lg:grid-cols-12 gap-6 mb-12">
          <div className="lg:col-span-5">
            <span className="section-eyebrow">01 · Services</span>
            <div className="divider-gold mt-2 mb-4" />
            <h2 className="font-display text-4xl sm:text-5xl text-navy">
              One <span className="italic text-royal">scholar</span>. Every service.
            </h2>
          </div>
          <p className="lg:col-span-6 lg:col-start-7 text-slate-600 text-base self-end">
            Everything a Ph.D. scholar, medical/nursing/law/management/engineering student, or researcher needs —
            from proofreading citations to hard-bound gold-embossed final thesis copies delivered to the door.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {SERVICES.map((s) => (<ServiceCard key={s.slug} service={s} />))}
        </div>
      </section>

      {/* HOW IT WORKS – dual flow */}
      <section className="bg-slate-50 border-y border-slate-200 py-20">
        <div className="container-app">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="section-eyebrow">02 · How it works</span>
            <div className="divider-gold mt-2 mb-4 mx-auto" />
            <h2 className="font-display text-4xl sm:text-5xl text-navy">Two focused workflows.</h2>
            <p className="mt-4 text-slate-600">Pick printing if your file is ready. Pick academic help if you want structured chapter-wise support.</p>
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            <FlowCard
              testid="flow-printing"
              badge="A · Printing"
              title="Upload → Bind → Deliver"
              steps={[
                "Upload your PDF or DOC",
                "Select print type, paper, binding & copies",
                "View instant cost",
                "Pay online securely",
                "Get printed & bound document delivered",
              ]}
              cta={{ to: "/upload", label: "Upload PDF" }}
              accent="royal"
            />
            <FlowCard
              testid="flow-academic"
              badge="B · Academic"
              title="Support → Review → Submit"
              steps={[
                "Select service (thesis, synopsis, paper, etc.)",
                "Submit topic, guidelines, deadline & files",
                "Get personalized quotation",
                "Work starts after confirmation",
                "Receive final Word/PDF document",
                "Order printing & binding if required",
              ]}
              cta={{ to: "/academic-request", label: "Request Help" }}
              accent="gold"
            />
          </div>
        </div>
      </section>

      {/* PRINTING & BINDING SHOWCASE */}
      <section className="container-app py-20 grid lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-5">
          <span className="section-eyebrow">03 · Printing & Binding</span>
          <div className="divider-gold mt-2 mb-4" />
          <h2 className="font-display text-4xl sm:text-5xl text-navy">Premium finishing that respects your years of work.</h2>
          <ul className="mt-8 space-y-3 text-sm text-slate-700">
            {[
              "Thesis, dissertation, synopsis, assignments & project reports",
              "Spiral · soft · hard · leather binding",
              "Golden and silver embossing on hard-bound covers",
              "Spine printing & premium 70–100 GSM paper options",
              "Colour and black-and-white printing",
              "Careful packaging and final thesis delivery",
            ].map((t, i) => (
              <li key={i} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-gold shrink-0 mt-0.5" /><span>{t}</span></li>
            ))}
          </ul>
          <div className="mt-8 flex gap-3">
            <Link to="/upload" className="btn-primary">Upload PDF</Link>
            <Link to="/calculator" className="btn-outline">Check Cost</Link>
          </div>
        </div>
        <div className="lg:col-span-7">
          <div className="grid grid-cols-6 grid-rows-2 gap-3 h-[420px]">
            <img src={PRESS_IMG} alt="Printing press machinery" className="col-span-4 row-span-2 border border-slate-200 rounded-sm w-full h-full object-cover" />
            <div className="col-span-2 border border-slate-200 rounded-sm p-5 flex flex-col justify-between bg-navy text-white">
              <div>
                <span className="font-mono text-[10px] uppercase tracking-widest text-gold">Hard bind</span>
                <div className="font-display text-3xl mt-1">₹200 <span className="text-xs text-slate-300">/ copy</span></div>
              </div>
              <div className="text-xs text-slate-300">Rich navy cloth · gold embossed spine</div>
            </div>
            <div className="col-span-2 border border-slate-200 rounded-sm p-5 flex flex-col justify-between bg-gold text-navy">
              <div>
                <span className="font-mono text-[10px] uppercase tracking-widest">Golden emboss</span>
                <div className="font-display text-3xl mt-1">₹50</div>
              </div>
              <div className="text-xs">Author name, university & year on spine</div>
            </div>
          </div>
        </div>
      </section>

      {/* PACKAGES */}
      <section className="bg-slate-50 border-y border-slate-200 py-20">
        <div className="container-app">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="section-eyebrow">04 · Packages</span>
            <div className="divider-gold mt-2 mb-4 mx-auto" />
            <h2 className="font-display text-4xl sm:text-5xl text-navy">Bundled for common scholar journeys.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            {PACKAGES.map((p) => (
              <div key={p.slug}
                   data-testid={`package-${p.slug}`}
                   className={`relative border rounded-sm p-6 flex flex-col ${p.highlight ? "bg-navy text-white border-navy" : "bg-white border-slate-200"}`}>
                {p.highlight && (
                  <span className="absolute -top-3 left-6 bg-gold text-navy text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm">Most Popular</span>
                )}
                <div className={`text-[10px] font-mono uppercase tracking-widest ${p.highlight ? "text-gold" : "text-royal"}`}>{p.tag}</div>
                <div className="font-display text-2xl mt-2">{p.name}</div>
                <div className={`text-xs mt-1 ${p.highlight ? "text-slate-300" : "text-slate-500"}`}>{p.price}</div>
                <ul className={`mt-4 space-y-2 text-xs flex-1 ${p.highlight ? "text-slate-200" : "text-slate-600"}`}>
                  {p.features.map((f, i) => (
                    <li key={i} className="flex gap-2"><CheckCircle2 className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${p.highlight ? "text-gold" : "text-royal"}`} /><span>{f}</span></li>
                  ))}
                </ul>
                <Link to="/academic-request" className={`mt-6 text-sm font-medium inline-flex items-center gap-1 ${p.highlight ? "text-gold" : "text-royal"} hover:underline`}>
                  Choose plan <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="container-app py-20 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <span className="section-eyebrow">05 · Trust & Safety</span>
          <div className="divider-gold mt-2 mb-4" />
          <h2 className="font-display text-4xl text-navy">Confidential. Ethical. Delivered.</h2>
          <p className="mt-4 text-slate-600 text-sm">
            We help you edit, format, review citations, check plagiarism, and print — always as academic support.
            Your files stay private and are never shared with third parties.
          </p>
        </div>
        <div className="lg:col-span-7">
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              ["Confidential document handling", "Files stored in secure cloud, deleted on request."],
              ["Secure PDF upload", "End-to-end encrypted transfer."],
              ["Proof approval before print", "You approve a proof before we send to press."],
              ["Revision support", "Revisions included as per order terms."],
              ["Proper citation & formatting", "APA, MLA, IEEE, Vancouver, Bluebook."],
              ["Student-friendly pricing", "Transparent per-page and per-binding rates."],
            ].map(([t, d], i) => (
              <div key={i} className="border border-slate-200 rounded-sm p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Star className="w-4 h-4 text-gold" />
                  <div className="font-medium text-navy text-sm">{t}</div>
                </div>
                <div className="text-xs text-slate-600">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="container-app pb-24">
        <div className="relative overflow-hidden border border-slate-200 rounded-sm bg-navy text-white p-8 sm:p-12 grid lg:grid-cols-12 gap-6 items-center">
          <img src={GRAD_IMG} alt="Graduates" className="absolute inset-0 w-full h-full object-cover opacity-15" />
          <div className="relative lg:col-span-8">
            <span className="section-eyebrow text-gold">Ready?</span>
            <h3 className="font-display text-3xl sm:text-4xl mt-2">From research idea to final hard-bound thesis — one platform.</h3>
          </div>
          <div className="relative lg:col-span-4 flex flex-wrap gap-3">
            <Link to="/upload" className="btn-gold">Upload PDF</Link>
            <Link to="/academic-request" className="btn-outline border-white text-white hover:bg-white hover:text-navy">Request Help</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function FlowCard({ badge, title, steps, cta, accent = "royal", testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 rounded-sm p-8">
      <div className="flex items-center gap-3 mb-6">
        <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm border ${accent === "gold" ? "border-gold text-gold bg-gold/5" : "border-royal text-royal bg-royal/5"}`}>{badge}</span>
        <div className="h-px flex-1 bg-slate-200" />
      </div>
      <h3 className="font-display text-3xl text-navy">{title}</h3>
      <ol className="mt-6 space-y-3">
        {steps.map((s, i) => (
          <li key={i} className="flex gap-3 text-sm">
            <span className={`shrink-0 w-6 h-6 flex items-center justify-center font-mono text-[11px] rounded-sm border ${accent === "gold" ? "border-gold text-navy bg-gold/20" : "border-royal text-royal bg-royal/5"}`}>{i + 1}</span>
            <span className="text-slate-700">{s}</span>
          </li>
        ))}
      </ol>
      <Link to={cta.to} className={accent === "gold" ? "btn-gold mt-8 inline-flex" : "btn-primary mt-8 inline-flex"}>
        {cta.label} <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
}
